import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { authAPI } from "../lib/studentAPI";
import { ArrowLeft, Loader2, CheckCircle, XCircle } from "lucide-react";

export const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{
    show: boolean;
    type: "success" | "error";
    message: string;
  }>({ show: false, type: "success", message: "" });

  const navigate = useNavigate();

  // Auto hide modal after 3 seconds
  useEffect(() => {
    if (modal.show) {
      const timer = setTimeout(() => {
        setModal((prev) => ({ ...prev, show: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [modal.show]);

  const showModal = (type: "success" | "error", message: string) => {
    setModal({ show: true, type, message });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!name.trim() || !email.trim() || !password || !confirmPassword) {
      return showModal("error", "All fields are required");
    }

    if (password !== confirmPassword) {
      return showModal("error", "Passwords do not match");
    }

    if (password.length < 8) {
      return showModal("error", "Password must be at least 8 characters");
    }

    setLoading(true);

    try {
      const response = await authAPI.register({
        name: name.trim(),
        email: email.trim(),
        password,
      });

      // You can still call login() here if you want to auto-login
      // login(response.token, response.student);

      showModal("success", "Account created successfully! Redirecting to login...");

      // Redirect to login after modal shows
      setTimeout(() => {
        navigate("/login");
      }, 3200); // slightly longer than modal timeout
    } catch (error: any) {
      showModal("error", error.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate("/")} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8">
          <h1 className="text-center text-3xl font-bold mb-2 text-gray-800">
            Create Account
          </h1>
          <p className="text-center text-gray-500 mb-8">
            Start learning today
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>

            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            <div>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            {/* Green Submit Button - Clean & Always Visible */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3.5 rounded-2xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2 text-base"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-green-600 hover:underline font-medium">
              Login
            </Link>
          </p>
        </div>
      </motion.div>

      {/* Animated Message Modal */}
      <AnimatePresence>
        {modal.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.1, ease: "easeOut" }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center"
            >
              <div className="flex justify-center mb-4">
                {modal.type === "success" ? (
                  <CheckCircle className="h-16 w-16 text-green-500" />
                ) : (
                  <XCircle className="h-16 w-16 text-red-500" />
                )}
              </div>

              <h3 className="text-2xl font-semibold mb-2">
                {modal.type === "success" ? "Success!" : "Oops!"}
              </h3>

              <p className="text-gray-600 leading-relaxed">{modal.message}</p>

              {/* Optional: Progress bar that fills in 3 seconds */}
              <div className="h-1 bg-gray-100 rounded-full mt-6 overflow-hidden">
                <motion.div
                  className="h-full bg-green-500"
                  initial={{ width: "100%" }}
                  animate={{ width: "0%" }}
                  transition={{ duration: 1, ease: "linear" }}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};